import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import type { ThematicGenerationRun } from '@/types/thematicMaps';

export const useGenerationRuns = (mapId: string | undefined) => {
  const [runs, setRuns] = useState<ThematicGenerationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchRuns = useCallback(async () => {
    if (!user || !mapId) return;

    try {
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from('thematic_generation_runs')
        .select('*')
        .eq('map_id', mapId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRuns((data || []) as ThematicGenerationRun[]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd pobierania logów AI';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, mapId, toast]);

  const createRun = useCallback(async (
    runData: Omit<ThematicGenerationRun, 'id' | 'created_at'>
  ): Promise<ThematicGenerationRun | null> => {
    try {
      const { data, error } = await (supabase as any)
        .from('thematic_generation_runs')
        .insert(runData)
        .select()
        .single();

      if (error) throw error;

      setRuns(prev => [data as ThematicGenerationRun, ...prev]);
      return data as ThematicGenerationRun;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd logowania AI run';
      console.error(msg);
      return null;
    }
  }, []);

  const updateRun = useCallback(async (
    id: string,
    updates: Partial<ThematicGenerationRun>
  ): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_generation_runs')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setRuns(prev => prev.map(r =>
        r.id === id ? { ...r, ...updates } : r
      ));
      return true;
    } catch (err) {
      console.error('Error updating run:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    if (user?.id && mapId) {
      fetchRuns();
    }
  }, [user?.id, mapId, fetchRuns]);

  return {
    runs,
    loading,
    createRun,
    updateRun,
    refreshRuns: fetchRuns,
  };
};
